import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Calendar, DateRangePicker, Divider, InputNumber, Message, Panel, SelectPicker, Stack, toaster } from 'rsuite';
import { Button as NsiButton, Modal } from '@nsi-advisory-services/components';

import {
  useActiveTimeOffSource,
  useCreateLocalTimeOffRange,
  useDeleteLocalTimeOffEntry,
  useReplaceLocalTimeOffEntryRange,
  useTimeOffCalendar,
  useUsers,
} from '../hooks/useQueries';

const isIsoDate = (s) => /^\d{4}-\d{2}-\d{2}$/.test(String(s || '').trim());

const toIsoDate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const formatDisplayDate = (iso) => {
  if (!isIsoDate(iso)) return '—';
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const parseIsoToDate = (iso) => {
  if (!isIsoDate(iso)) return null;
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
};

const weekdayCountInRange = (startDate, endDate) => {
  if (!(startDate instanceof Date) || Number.isNaN(startDate.getTime())) return 0;
  if (!(endDate instanceof Date) || Number.isNaN(endDate.getTime())) return 0;

  const start = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
  const end = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
  if (start.getTime() > end.getTime()) return 0;

  let count = 0;
  const current = new Date(start);
  while (current.getTime() <= end.getTime()) {
    const day = current.getDay();
    if (day >= 1 && day <= 5) count += 1;
    current.setDate(current.getDate() + 1);
  }

  return count;
};

const monthStartIso = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-01`;
};

const isWeekdayDate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return false;
  const day = date.getDay();
  return day >= 1 && day <= 5;
};

const formatHours = (hours) => {
  const n = Number(hours);
  if (!Number.isFinite(n)) return '0.00h';
  return `${n.toFixed(2)}h`;
};

const getEntryChipLabel = (entry) => {
  const name = String(entry?.userName || '').trim();
  const shortName = name.length > 12 ? `${name.slice(0, 12)}…` : (name || `User ${entry?.userId ?? ''}`);
  return `${shortName} ${formatHours(entry?.hours)}`;
};

export default function TimeOffManagerPage() {
  const navigate = useNavigate();
  const { data: activeTimeOffSource, isLoading: activeTimeOffSourceLoading } = useActiveTimeOffSource();
  const activeTimeOffSourceName = String(activeTimeOffSource?.name || 'manual').toLowerCase();
  const isManualSource = activeTimeOffSourceName === 'manual';

  const { data: users = [], isLoading: usersLoading, error: usersError } = useUsers();
  const userById = useMemo(() => {
    const map = new Map();
    (Array.isArray(users) ? users : []).forEach((u) => {
      const id = Number(u?.id);
      if (!Number.isInteger(id)) return;
      const name = String(u?.name || u?.displayName || u?.email || `User ${id}`);
      map.set(id, name);
    });
    return map;
  }, [users]);

  const userOptions = useMemo(
    () =>
      (Array.isArray(users) ? users : [])
        .map((u) => {
          const id = Number(u?.id);
          const label = String(u?.name || u?.displayName || u?.email || `User ${id}`);
          return { label, value: id };
        })
        .filter((u) => Number.isInteger(u.value) && u.label),
    [users]
  );

  const [calendarDate, setCalendarDate] = useState(() => new Date());
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [entryModalOpen, setEntryModalOpen] = useState(false);
  const [entryModalMode, setEntryModalMode] = useState('create');
  const [editingEntryId, setEditingEntryId] = useState(null);
  const [modalUserId, setModalUserId] = useState(null);
  const [modalRange, setModalRange] = useState(null);
  const [modalDate, setModalDate] = useState(() => new Date());
  const [modalHours, setModalHours] = useState(8);

  const monthStart = useMemo(() => monthStartIso(calendarDate), [calendarDate]);
  const {
    data: calendarData,
    isLoading: calendarLoading,
    error: calendarError,
  } = useTimeOffCalendar(monthStart, { enabled: isManualSource });

  const createRangeMutation = useCreateLocalTimeOffRange();
  const replaceEntryRangeMutation = useReplaceLocalTimeOffEntryRange();
  const deleteEntryMutation = useDeleteLocalTimeOffEntry();

  const entries = useMemo(() => {
    const rows = Array.isArray(calendarData?.entries) ? calendarData.entries : [];
    return rows
      .map((r) => ({
        id: Number(r?.id),
        userId: Number(r?.userId ?? r?.user_id),
        userName: String(r?.userName || r?.user_name || ''),
        date: String(r?.date || ''),
        hours: Number(r?.hours ?? 0),
      }))
      .filter((r) => Number.isInteger(r.id) && Number.isInteger(r.userId) && isIsoDate(r.date));
  }, [calendarData?.entries]);

  const entriesByDate = useMemo(() => {
    const map = new Map();
    for (const e of entries) {
      const dateKey = String(e?.date || '').slice(0, 10);
      if (!isIsoDate(dateKey)) continue;
      if (!map.has(dateKey)) map.set(dateKey, []);
      map.get(dateKey).push(e);
    }
    for (const [dateKey, list] of map.entries()) {
      list.sort((a, b) => {
        const nameA = String(a?.userName || userById.get(a?.userId) || '');
        const nameB = String(b?.userName || userById.get(b?.userId) || '');
        return nameA.localeCompare(nameB);
      });
      map.set(dateKey, list);
    }
    return map;
  }, [entries, userById]);

  const selectedDateIso = useMemo(() => toIsoDate(selectedDate), [selectedDate]);

  const selectedDateEntries = useMemo(() => {
    if (!selectedDateIso) return [];
    return entriesByDate.get(selectedDateIso) || [];
  }, [entriesByDate, selectedDateIso]);

  const modalUserLabel = useMemo(() => {
    const match = userOptions.find((u) => Number(u.value) === Number(modalUserId));
    return match?.label || '';
  }, [modalUserId, userOptions]);

  const [rangeStartDate, rangeEndDate] = Array.isArray(modalRange) ? modalRange : [null, null];
  const modalRangeWeekdayCount = useMemo(
    () => weekdayCountInRange(rangeStartDate, rangeEndDate),
    [rangeStartDate, rangeEndDate]
  );

  const savePending = createRangeMutation.isPending || replaceEntryRangeMutation.isPending;

  const canSave = entryModalMode === 'edit'
    ? (
      Number.isInteger(modalUserId)
      && rangeStartDate instanceof Date
      && !Number.isNaN(rangeStartDate.getTime())
      && rangeEndDate instanceof Date
      && !Number.isNaN(rangeEndDate.getTime())
      && Number.isFinite(Number(modalHours))
      && Number(modalHours) >= 0
    )
    : (
      Number.isInteger(modalUserId)
      && rangeStartDate instanceof Date
      && !Number.isNaN(rangeStartDate.getTime())
      && rangeEndDate instanceof Date
      && !Number.isNaN(rangeEndDate.getTime())
      && Number.isFinite(Number(modalHours))
      && Number(modalHours) >= 0
    );

  useEffect(() => {
    if (!entryModalOpen || entryModalMode !== 'create') return;
    setModalHours(modalRangeWeekdayCount * 8);
  }, [entryModalMode, entryModalOpen, modalRangeWeekdayCount]);

  const resetEntryModal = () => {
    setEntryModalMode('create');
    setEditingEntryId(null);
    setModalUserId(null);
    setModalRange(null);
    setModalDate(new Date());
    setModalHours(8);
  };

  const openCreateForDate = (date) => {
    const d = date instanceof Date ? new Date(date) : new Date();
    setEntryModalMode('create');
    setEditingEntryId(null);
    setModalUserId(userOptions[0]?.value ?? null);
    setModalRange([d, d]);
    setModalDate(d);
    setModalHours(isWeekdayDate(d) ? 8 : 0);
    setEntryModalOpen(true);
  };

  const openEditEntry = (entry) => {
    const editDate = parseIsoToDate(entry?.date) || new Date();
    setEntryModalMode('edit');
    setEditingEntryId(Number(entry?.id));
    setModalUserId(Number(entry?.userId));
    setModalRange([editDate, editDate]);
    setModalDate(editDate);
    setModalHours(Number(entry?.hours ?? 0));
    setEntryModalOpen(true);
  };

  const handleSave = async () => {
    if (!canSave) return;
    const startIso = toIsoDate(rangeStartDate);
    const endIso = toIsoDate(rangeEndDate);

    try {
      if (entryModalMode === 'edit' && Number.isInteger(editingEntryId)) {
        if (!startIso || !endIso) return;
        await replaceEntryRangeMutation.mutateAsync({
          entryId: Number(editingEntryId),
          userId: Number(modalUserId),
          startDate: startIso,
          endDate: endIso,
          totalHours: Number(modalHours),
        });
      } else {
        if (!startIso || !endIso) return;
        await createRangeMutation.mutateAsync({
          userId: Number(modalUserId),
          startDate: startIso,
          endDate: endIso,
          totalHours: Number(modalHours),
        });
      }

      toaster.push(
        <Message type="success" showIcon>
          {entryModalMode === 'edit' ? 'Updated time off entry.' : `Added time off for ${modalUserLabel || 'user'}.`}
        </Message>,
        { placement: 'topEnd', duration: 2500 }
      );

      const focusDate = rangeStartDate || modalDate;
      setSelectedDate(focusDate);
      setCalendarDate(new Date(focusDate.getFullYear(), focusDate.getMonth(), 1));
      setEntryModalOpen(false);
      resetEntryModal();
    } catch (err) {
      toaster.push(
        <Message type="error" showIcon>
          {String(err?.message || 'Failed to save time off entry.')}
        </Message>,
        { placement: 'topEnd', duration: 4500 }
      );
    }
  };

  const handleDelete = async () => {
    if (!Number.isInteger(editingEntryId)) return;
    try {
      await deleteEntryMutation.mutateAsync({ entryId: editingEntryId, userId: modalUserId });
      toaster.push(
        <Message type="success" showIcon>
          Deleted time off entry.
        </Message>,
        { placement: 'topEnd', duration: 2500 }
      );
      setEntryModalOpen(false);
      resetEntryModal();
    } catch (err) {
      toaster.push(
        <Message type="error" showIcon>
          {String(err?.message || 'Failed to delete time off entry.')}
        </Message>,
        { placement: 'topEnd', duration: 4500 }
      );
    }
  };

  const renderCalendarCell = (date) => {
    const dateIso = toIsoDate(date);
    if (!dateIso) return null;

    const dayEntries = entriesByDate.get(dateIso) || [];
    if (dayEntries.length === 0) return null;

    const visible = dayEntries.slice(0, 3);
    const hiddenCount = Math.max(0, dayEntries.length - visible.length);

    return (
      <div style={{ marginTop: 4, display: 'grid', gap: 3 }}>
        {visible.map((entry) => (
          <button
            key={`entry-chip-${entry.id}`}
            type="button"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setSelectedDate(date);
              openEditEntry(entry);
            }}
            style={{
              display: 'block',
              width: '100%',
              textAlign: 'left',
              border: '1px solid #d6e4ff',
              background: '#f0f5ff',
              color: '#1d39c4',
              borderRadius: 6,
              padding: '2px 5px',
              fontSize: 10,
              lineHeight: 1.25,
              cursor: 'pointer',
            }}
            title={`${entry.userName || userById.get(entry.userId) || `User ${entry.userId}`} • ${formatHours(entry.hours)} • Click to edit`}
          >
            {getEntryChipLabel(entry)}
          </button>
        ))}
        {hiddenCount > 0 ? (
          <div style={{ fontSize: 10, color: '#666', paddingLeft: 2 }}>
            +{hiddenCount} more
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div className="ppc-page">
      <Panel
        bordered
        header={(
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <span>Time Off Manager</span>
            <Button appearance="subtle" size="sm" onClick={() => navigate('/configuration')}>
              Back to Configuration
            </Button>
          </div>
        )}
      >
        {activeTimeOffSourceLoading ? (
          <Message type="info" showIcon>
            Loading time off source...
          </Message>
        ) : null}

        {!activeTimeOffSourceLoading && !isManualSource ? (
          <Message type="info" showIcon>
            Time Off Source is set to EmployeeConnect. Switch it to Manual in Integrations to edit local time off days.
          </Message>
        ) : null}

        {usersError ? (
          <Message type="error" showIcon>
            Failed to load users.
          </Message>
        ) : null}

        {!activeTimeOffSourceLoading && isManualSource ? (
          <>
            <Stack justifyContent="space-between" alignItems="center" wrap spacing={8} style={{ marginBottom: 10 }}>
              <Button
                appearance="primary"
                onClick={() => openCreateForDate(selectedDate)}
              >
                Add Time Off
              </Button>
            </Stack>

            {calendarError ? (
              <Message type="error" showIcon>
                Failed to load calendar entries.
              </Message>
            ) : null}

            <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: 8 }}>
              <Calendar
                bordered
                value={calendarDate}
                weekStart={0}
                onChange={(date) => {
                  setCalendarDate(date);
                  setSelectedDate(date);
                }}
                renderCell={renderCalendarCell}
                style={{ width: '100%' }}
              />
            </div>

            <Divider />

            <Panel
              bordered
              header={<div style={{ fontWeight: 700 }}>Selected Date: {formatDisplayDate(selectedDateIso)}</div>}
            >

              {calendarLoading ? (
                <div style={{ color: '#666' }}>Loading entries…</div>
              ) : selectedDateEntries.length === 0 ? (
                <Message type="info" showIcon>
                  No time off entries on this date.
                </Message>
              ) : (
                <div style={{ display: 'grid', gap: 8 }}>
                  {selectedDateEntries.map((entry) => (
                    <button
                      type="button"
                      key={`selected-date-entry-${entry.id}`}
                      onClick={() => openEditEntry(entry)}
                      style={{
                        textAlign: 'left',
                        border: '1px solid #d9d9d9',
                        background: '#fff',
                        borderRadius: 8,
                        padding: '10px 12px',
                        cursor: 'pointer',
                      }}
                    >
                      <div style={{ fontWeight: 700, color: '#1f1f1f' }}>
                        {entry.userName || userById.get(entry.userId) || `User ${entry.userId}`}
                      </div>
                      <div style={{ marginTop: 4, color: '#666', fontSize: 12 }}>
                        {formatHours(entry.hours)} • Click to edit
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </Panel>
          </>
        ) : null}
      </Panel>

      <Modal
        open={entryModalOpen}
        size="sm"
        onClose={() => {
          if (savePending || deleteEntryMutation.isPending) return;
          setEntryModalOpen(false);
          resetEntryModal();
        }}
      >
        <Modal.Header>
          <Modal.Title>{entryModalMode === 'edit' ? 'Edit Time Off' : 'Add Time Off'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Stack direction="column" spacing={10}>
            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Employee</div>
              <SelectPicker
                block
                searchable
                cleanable={false}
                loading={usersLoading}
                placeholder="Select employee"
                data={userOptions}
                value={modalUserId}
                onChange={(value) => {
                  const parsed = Number(value);
                  setModalUserId(Number.isInteger(parsed) ? parsed : null);
                }}
              />
            </div>

            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Date Range</div>
              <DateRangePicker
                block
                cleanable={false}
                editable={false}
                weekStart={0}
                format="MM/dd/yyyy"
                placeholder="Select start and end dates"
                value={modalRange}
                onChange={(value) => {
                  setModalRange(Array.isArray(value) ? value : null);
                }}
              />
              <div style={{ marginTop: 6, fontSize: 12, color: '#666' }}>
                {modalRangeWeekdayCount > 0
                  ? `Range includes ${modalRangeWeekdayCount} weekday${modalRangeWeekdayCount === 1 ? '' : 's'}. Default total is ${(modalRangeWeekdayCount * 8).toFixed(2)}h (weekends excluded). You can manually adjust hours.`
                  : 'Range has no weekdays. You can still save, and default hours are 0.'}
              </div>
            </div>

            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Hours</div>
              <InputNumber
                block
                min={0}
                step={0.25}
                precision={2}
                value={modalHours}
                onChange={(value) => {
                  const parsed = Number(value);
                  setModalHours(Number.isFinite(parsed) ? Math.max(0, parsed) : 0);
                }}
              />
            </div>

            {entryModalMode !== 'edit' && modalRangeWeekdayCount === 0 ? (
              <Message type="info" showIcon>
                Selected range has no weekdays.
              </Message>
            ) : null}
          </Stack>
        </Modal.Body>
        <Modal.Footer
          left={
            entryModalMode === 'edit' ? (
              <NsiButton
                variant="danger"
                loading={deleteEntryMutation.isPending}
                disabled={savePending || !Number.isInteger(editingEntryId)}
                onClick={() => {
                  void handleDelete();
                }}
              >
                Delete
              </NsiButton>
            ) : null
          }
        >
          <NsiButton
            disabled={savePending || deleteEntryMutation.isPending}
            onClick={() => {
              setEntryModalOpen(false);
              resetEntryModal();
            }}
          >
            Cancel
          </NsiButton>
          <NsiButton
            variant="primary"
            loading={savePending}
            disabled={!canSave || deleteEntryMutation.isPending}
            onClick={() => {
              void handleSave();
            }}
          >
            {entryModalMode === 'edit' ? 'Save Changes' : 'Add Time Off'}
          </NsiButton>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
